import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation as useInterfaceLanguage } from 'react-i18next'
import { tr } from 'i18n'
import { UserRound, Warehouse, History, LoaderCircle, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react'
import { EmptyState } from 'components/Erp/Presentation'
import { getRequestHistory } from 'service/RequestsService'
import { getReportRequestHistory } from 'service/ReportsService'
import { getErpErrorMessage } from 'service/ErpService'
import RequestHistoryEvent from '../RequestHistoryEvent/RequestHistoryEvent.tsx'
import s from 'pages/AdminPanelPage/Management.module.scss'
import './RequestTimeline.scss'

export default function RequestTimeline({
  section = "",
  objectId = "",
  driver = "",
  grouped = false,
  readOnly = false,
}) {
  useInterfaceLanguage();
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [group, setGroup] = useState("all");
  const [action, setAction] = useState("");
  const [direction, setDirection] = useState("");
  const [query, setQuery] = useState("");
  const sequence = useRef(0);
  const load = useCallback(async () => {
    const current = ++sequence.current;
    setLoading(true);
    setError("");
    try {
      const { data } = await (readOnly ? getReportRequestHistory : getRequestHistory)({
        section: section || undefined,
        object_id: objectId || undefined,
        driver: driver || undefined,
        action: action || undefined,
        request_type: direction || undefined,
        search: query || undefined,
        page,
        page_size: 25,
      });
      if (current !== sequence.current) return;
      setRecords(data.results || data || []);
      setTotal(data.count ?? (data.results || data || []).length);
    } catch (e) {
      if (current === sequence.current) setError(getErpErrorMessage(e, tr("Не удалось загрузить историю.")));
    } finally {
      if (current === sequence.current) setLoading(false);
    }
  }, [section, objectId, driver, action, direction, query, page, readOnly]);
  useEffect(() => {
    load();
    return () => { sequence.current += 1; };
  }, [load]);
  const groups = records.reduce((result, item) => {
    const key =
      group === "driver"
        ? item.driver_name || tr("Водитель #{{value0}}", { value0: item.driver || "—" })
        : group === "base"
          ? item.base_name || tr("Без базы")
          : "";
    (result[key] ||= []).push(item);
    return result;
  }, {});
  return (
    <div className="request-history">
      <div className="request-history__heading"><strong>{tr('Событий: ')}{loading ? '—' : total}</strong>
        <button type="button" className={s.button} disabled={loading} onClick={load}><RefreshCw size={16} aria-hidden="true" />{tr('Обновить')}</button></div>
      {tr(grouped && (
        <div className={s.toolbar}>
          <select
            aria-label={tr("Группировка истории")}
            value={group}
            onChange={(e) => setGroup(e.target.value)}
          >
            <option value="all">{tr("Общая история")}</option>
            <option value="driver">{tr("По водителям")}</option>
            <option value="base">{tr("По базам")}</option>
          </select>
          <select
            aria-label={tr("Событие")}
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(1);
            }}
          >
            <option value="">{tr("Все события")}</option>
            <option value="approve">{tr("Подтверждения")}</option>
            <option value="reject">{tr("Отказы")}</option>
            <option value="entry">{tr("Вход в Кара-Кече")}</option>
            <option value="departure">{tr("Выход из Кара-Кече")}</option>
            <option value="arrival">{tr("Прибытие на базу")}</option>
            <option value="update">{tr("Изменения")}</option>
          </select>
          <select
            aria-label={tr("Направление заявок на базы")}
            value={direction}
            onChange={(e) => {
              setDirection(e.target.value);
              setPage(1);
            }}
          >
            <option value="">{tr("Вход и выход на базы")}</option>
            <option value="join">{tr("Вход на базу")}</option>
            <option value="leave">{tr("Выход с базы")}</option>
          </select>
          <input
            aria-label={tr("Поиск в истории")}
            placeholder={tr("Водитель, база, пользователь")}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
          />
        </div>
      ))}
      {tr(error && (
        <div className={s.error} role="alert">
          {tr(error)}
          <button className={s.button} onClick={load}><RefreshCw size={14} aria-hidden="true"/>{tr("Повторить")}</button>
        </div>
      ))}
      {loading ? (
        <EmptyState icon={LoaderCircle} title={tr("Загрузка истории…")} compact />
      ) : !records.length && error ? null : !records.length ? (
        <EmptyState icon={History} title={tr("Событий пока нет")} description={tr("Здесь появятся решения по заявкам и изменения с указанием времени и ответственного.")} compact />
      ) : (
        Object.entries(groups).map(([title, items]) => (
          <div key={title}>
            {tr(title && <h3 className={s.groupTitle}>{group === "driver" ? <UserRound size={16} aria-hidden="true"/> : <Warehouse size={16} aria-hidden="true"/>}{tr(title)}</h3>)}
            <div className="request-history__events">
              {items.map((item) => (
                <RequestHistoryEvent key={item.id} item={item} />
              ))}
            </div>
          </div>
        ))
      )}
      {tr(total > 25 && (
        <div className={s.pagination}>
          <span>{tr("Событий: ")}{tr(total)}</span>
          <button
            className={s.button}
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          ><ChevronLeft size={14} aria-hidden="true"/>{tr("Назад")}</button>
          <span>{tr("Страница ")}{tr(page)}</span>
          <button
            className={s.button}
            disabled={page * 25 >= total}
            onClick={() => setPage((p) => p + 1)}
          ><ChevronRight size={14} aria-hidden="true"/>{tr("Далее")}</button>
        </div>
      ))}
    </div>
  );
}
